import { itemTemplateApi } from "#/api/itemTemplateApi";
import { productApi } from "#/api/productApi";
import { sitesApi } from "#/api/sitesApi";
import ActionButton from "#/components/buttons/ActionButton";
import DetailPageLayout from "#/components/ui/DetailPageLayout";
import {
  ItemType,
  MaintenanceStatus,
  RetrievalStatus,
} from "#/types/items";
import type { CreateItemTemplatePayload, ItemTemplate } from "#/types/itemTemplate";
import { sanitize, sanitizeItemStatusLabel } from "#/utils/StringUtils";
import {
  Accordion,
  Button,
  Select,
  TextInput,
  Textarea,
} from "@mantine/core";
import { useForm } from "@mantine/form";
import { IconAlertTriangle, IconPlus, IconTrash } from "@tabler/icons-react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  showErrorNotification,
  showSuccessNotification,
} from "#/components/notifications";
import { useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate, useParams } from "react-router-dom";
import { usePermission } from "#/hooks/usePermission";
import PermissionTooltip from "#/components/ui/PermissionTooltip";
import { useUnsavedChangesWarning } from "#/hooks/useUnsavedChangesWarning";

/** The template as the form holds it. Empty = "" for text, null for selects. */
const toFormValues = (template: ItemTemplate): CreateItemTemplatePayload => ({
  name: template.name,
  description: template.description ?? "",
  site_id: template.site?.id.toString() ?? null,
  product_id: template.product?.id.toString() ?? null,
  notify_email: template.notify_email ?? "",
  location: template.location ?? "",
  customize_columns: template.customize_columns ?? {},
  status: template.status ?? null,
  maintenance_status: template.maintenance_status ?? null,
  retrieval_status: template.retrieval_status ?? null,
  type: template.type ?? null,
});

const TemplateDetailPage = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { id } = useParams();
  const { canWrite } = usePermission();

  const emptyForm: CreateItemTemplatePayload = {
    name: "",
    description: "",
    product_id: null,
    site_id: null,
    notify_email: "",
    location: "",
    customize_columns: {},
    status: null,
    maintenance_status: null,
    retrieval_status: null,
    type: null,
  };

  // Labels of the selected category's scan steps, kept in a ref so the form's
  // validate rules (closures created once) always see the latest set.
  const categoryStepLabelsRef = useRef<Set<string> | null>(null);

  const form = useForm<CreateItemTemplatePayload>({
    initialValues: emptyForm,
    validate: {
      name: (value) => (value ? null : t("templates.validation.templateNameRequired")),
      status: (value, values) => {
        // No category: a template cannot carry a status, nothing to validate.
        if (!values.product_id) return null;
        if (!value) return t("items.statusRules.statusRequired");
        const steps = categoryStepLabelsRef.current;
        // Steps still loading: let the backend be the judge rather than block.
        if (steps === null) return null;
        return steps.has(value)
          ? null
          : `${sanitizeItemStatusLabel(value)} — ${t("items.statusRules.statusNotInCategory")}`;
      },
    },
  });

  useUnsavedChangesWarning(form.isDirty());

  const { data: { products = [] } = {} } = useQuery({
    queryKey: ["products"],
    queryFn: async () => {
      const response = await productApi.getAll({
        page: 1,
        limit: 10000,
      });
      if (!response.data) {
        throw new Error("Failed to fetch categories");
      }
      return {
        products: response.data.data,
      };
    },
    enabled: true,
    staleTime: 1000 * 60,
  });

  const { data: { sites = [] } = {} } = useQuery({
    queryKey: ["sites"],
    queryFn: async () => {
      const response = await sitesApi.getAll({ page: 1, limit: 10000 });
      if (!response.data) {
        throw new Error("Failed to fetch sites");
      }
      return {
        sites: response.data.data,
      };
    },
    enabled: true,
    staleTime: 1000 * 60,
  });

  const selectedProductId = form.values.product_id ? Number(form.values.product_id) : null;

  // The selected category's scan steps: the only statuses a template may carry.
  const { data: categorySteps, isPending: categoryStepsPending } = useQuery({
    queryKey: ["product-statuses", selectedProductId],
    queryFn: async () => {
      const response = await productApi.getStatuses(selectedProductId as number);
      if (!response.data) {
        throw new Error("Failed to fetch category scan steps");
      }
      return [...response.data].sort((a, b) => a.step - b.step);
    },
    enabled: selectedProductId !== null,
    staleTime: 1000 * 60,
  });
  const stepsLoaded = selectedProductId !== null && !categoryStepsPending;
  const categoryStepLabels = useMemo(() => {
    if (!stepsLoaded) return null;
    return new Set(
      (categorySteps ?? []).map((step) => step.label).filter((label): label is string => !!label),
    );
  }, [stepsLoaded, categorySteps]);
  categoryStepLabelsRef.current = selectedProductId === null ? new Set() : categoryStepLabels;

  // After the USER changes the category, a status that is not a step of the new
  // category is dropped once the steps are known. Loading an existing template
  // never triggers this: its (possibly wrong) status must stay visible to be fixed.
  const pendingStatusRecheckRef = useRef(false);
  const [showCategoryClearedHint, setShowCategoryClearedHint] = useState(false);
  useEffect(() => {
    if (!pendingStatusRecheckRef.current || !categoryStepLabels) return;
    pendingStatusRecheckRef.current = false;
    const current = form.values.status;
    if (current && !categoryStepLabels.has(current)) {
      form.setFieldValue("status", null);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps -- runs when the steps for the new category arrive
  }, [categoryStepLabels]);

  const handleCategoryChange = (value: string | null) => {
    setShowCategoryClearedHint(false);
    if (value === null) {
      // No category, no status: a status only exists as one of the category's steps.
      if (form.values.status) setShowCategoryClearedHint(true);
      form.setFieldValue("status", null);
      form.setFieldValue("product_id", null);
      return;
    }
    pendingStatusRecheckRef.current = true;
    form.setFieldValue("product_id", value);
  };

  const statusOptions = useMemo(() => {
    const options = (categorySteps ?? [])
      .filter((step) => !!step.label)
      .map((step) => ({ value: step.label, label: sanitizeItemStatusLabel(step.label) }));
    const current = form.values.status;
    // A stored status that is not a step of its category stays selectable, flagged, so it can be fixed.
    if (current && stepsLoaded && !options.some((option) => option.value === current)) {
      options.push({
        value: current,
        label: `${sanitizeItemStatusLabel(current)} (${t("items.statusRules.statusNotInCategory")})`,
      });
    }
    return options;
  }, [categorySteps, form.values.status, stepsLoaded, t]);
  const categoryHasNoSteps = stepsLoaded && (categorySteps?.length ?? 0) === 0;

  const templateQuery = useQuery({
    queryKey: ["template-details", id],
    queryFn: async () => {
      const response = await itemTemplateApi.getById(id as string);
      if (!response.data) {
        throw new Error("Failed to fetch template");
      }
      return response.data;
    },
    enabled: id !== undefined && id !== "new",
  });

  // Load the fetched template into the form once, and never over unsaved edits:
  // a focus refetch must not wipe what the user is typing. While the form is
  // dirty the fetched object waits; it is applied after the next save.
  const appliedTemplateRef = useRef<ItemTemplate | null>(null);
  useEffect(() => {
    const template = templateQuery.data;
    if (!template || appliedTemplateRef.current === template || form.isDirty()) return;
    const values = toFormValues(template);
    form.setInitialValues(values);
    form.setValues(values);
    form.resetDirty(values);
    appliedTemplateRef.current = template;
  }, [templateQuery.data, form]);

  /**
   * What goes over the wire. `undefined` is dropped; on update `null` / `""`
   * are deliberate clears and are sent (the backend writes NULL); on create
   * empties are simply left out. An emptied custom-fields object is `null`.
   */
  const toSavePayload = (
    values: typeof form.values,
    mode: "create" | "update",
  ): Partial<CreateItemTemplatePayload> => {
    const payload: Partial<CreateItemTemplatePayload> = {};
    const isEmpty = (value: unknown) => value === null || value === "";

    (Object.entries(values) as [keyof CreateItemTemplatePayload, CreateItemTemplatePayload[keyof CreateItemTemplatePayload]][]).forEach(([key, value]) => {
      if (value === undefined) return;
      if (key === "customize_columns") {
        const columns = (value as Record<string, string> | null) ?? {};
        const hasKeys = Object.keys(columns).length > 0;
        if (hasKeys) payload.customize_columns = columns;
        else if (mode === "update") payload.customize_columns = null;
        return;
      }
      if (mode === "create" && isEmpty(value)) return;
      if (key === "site_id" || key === "product_id") {
        payload[key] = value === null || value === "" ? null : Number(value);
        return;
      }
      (payload as Record<string, CreateItemTemplatePayload[keyof CreateItemTemplatePayload]>)[key as string] = value;
    });

    if (mode === "create") {
      // A status only exists as a step of a category.
      if (payload.product_id == null) delete payload.status;
    } else if (payload.product_id === null) {
      payload.status = null;
    }

    return payload;
  };

  const handleSubmit = (values: typeof form.values) => {
    const payload = toSavePayload(values, id === "new" ? "create" : "update");
    templateMutation.mutate(payload as CreateItemTemplatePayload);
  };

  const templateMutation = useMutation({
    mutationFn: async (payload: CreateItemTemplatePayload) => {
      if (id !== "new") {
        return await itemTemplateApi.update(Number(id), payload);
      } else {
        return await itemTemplateApi.create(payload);
      }
    },
    onSuccess: (data) => {
      if (id === "new") {
        void navigate(`/inventory/templates/${data.data.id}`);
      } else {
        // What the form holds is now what is stored: make it the clean baseline,
        // then let the refetched template flow in through the apply effect.
        form.setInitialValues(form.values);
        form.resetDirty(form.values);
        setShowCategoryClearedHint(false);
        void queryClient.invalidateQueries({ queryKey: ["template-details", id] });
      }
      showSuccessNotification({
        message: id === "new" ? t("templates.createdSuccess") : t("templates.updatedSuccess"),
      });
    },
    onError: (error) => {
      showErrorNotification({
        message: error.message ?? t("common.failedToProcess"),
      });
    },
  });

  return (
    <DetailPageLayout
      title={id != "new" ? t("templates.templateDetails") : t("templates.createTemplate")}
      loading={templateMutation.isPending || templateQuery.isLoading}
      navRightSection={
        <div className={`w-full flex gap-2 items-center`}>
          <Button
            color="black"
            variant="outline"
            type="reset"
            disabled={!form.isDirty()}
            onClick={() => {
              form.reset();
            }}
            size="xs"
          >
            {t("common.clear")}
          </Button>

          <PermissionTooltip disabled={!canWrite()}>
            <Button
              color="black"
              variant="filled"
              loading={templateMutation.isPending}
              disabled={!form.isDirty() || !canWrite()}
              onClick={() => {
                form.onSubmit(handleSubmit)();
              }}
              size="xs"
            >
              {t("common.save")}
            </Button>
          </PermissionTooltip>
        </div>
      }
    >
      <div className="w-full flex flex-col">
        <form
          className="space-y-8 relative"
          onSubmit={form.onSubmit(handleSubmit)}
        >
          <Accordion
            variant={"separated"}
            defaultValue={["template_info", "item_defaults", "customize_columns"]}
            multiple
          >
            <Accordion.Item value={"template_info"}>
              <Accordion.Control>{t("templates.templateDetails")}</Accordion.Control>
              <Accordion.Panel>
                <div className="w-full grid grid-cols-1 gap-4">
                  <TextInput
                    label={t("templates.form.templateName")}
                    withAsterisk
                    disabled={!canWrite() || templateQuery.isLoading || templateMutation.isPending}
                    placeholder={t("templates.form.templateNamePlaceholder")}
                    {...form.getInputProps("name")}
                  />

                  <Textarea
                    label={t("templates.form.description")}
                    disabled={!canWrite() || templateQuery.isLoading || templateMutation.isPending}
                    placeholder={t("templates.form.descriptionPlaceholder")}
                    rows={3}
                    {...form.getInputProps("description")}
                  />
                </div>
              </Accordion.Panel>
            </Accordion.Item>

            <Accordion.Item value={"item_defaults"}>
              <Accordion.Control>{t("templates.itemDefaults")}</Accordion.Control>
              <Accordion.Panel>
                <div className="w-full grid grid-cols-1 gap-4">
                  <div className="w-full grid grid-cols-3 gap-4">
                    <Select
                      label={t("templates.form.site")}
                      placeholder={t("templates.form.sitePlaceholder")}
                      searchable
                      disabled={
                        !canWrite() || templateQuery.isLoading || templateMutation.isPending
                      }
                      data={sites.map((site) => ({
                        label: site?.name ?? "",
                        value: site?.id?.toString() ?? "",
                      }))}
                      clearable
                      value={form.values.site_id === null ? null : String(form.values.site_id)}
                      error={form.errors.site_id}
                      onChange={(value) => form.setFieldValue("site_id", value)}
                    />

                    <Select
                      label={t("templates.form.category")}
                      placeholder={t("templates.form.categoryPlaceholder")}
                      searchable
                      disabled={
                        !canWrite() || templateQuery.isLoading || templateMutation.isPending
                      }
                      data={products.map((prod) => ({
                        label: prod?.name ?? "",
                        value: prod?.id?.toString() ?? "",
                      }))}
                      clearable
                      value={form.values.product_id === null || form.values.product_id === undefined ? null : String(form.values.product_id)}
                      error={form.errors.product_id}
                      onChange={handleCategoryChange}
                    />

                    <div className="flex flex-col gap-1">
                      <Select
                        label={t("templates.form.status")}
                        placeholder={
                          selectedProductId === null
                            ? t("items.statusRules.statusNeedsCategory")
                            : t("templates.form.statusPlaceholder")
                        }
                        searchable
                        disabled={!canWrite() || selectedProductId === null || categoryHasNoSteps}
                        data={statusOptions}
                        clearable
                        withAsterisk={selectedProductId !== null}
                        value={form.values.status ?? null}
                        error={form.errors.status}
                        onChange={(value) => form.setFieldValue("status", value)}
                      />
                      {categoryHasNoSteps && (
                        <div className="flex items-start gap-2 rounded-md border border-red-200 bg-red-50 px-3 py-2 text-xs text-red-700">
                          <IconAlertTriangle size={14} className="mt-0.5 shrink-0" />
                          <span>
                            {t("items.statusRules.categoryHasNoSteps")}{" "}
                            <a
                              href={`/inventory/categories/${selectedProductId}`}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="font-semibold underline underline-offset-2"
                            >
                              {t("items.statusRules.openCategory")}
                            </a>
                          </span>
                        </div>
                      )}
                      {showCategoryClearedHint && selectedProductId === null && (
                        <span className="text-xs text-amber-700">
                          {t("items.statusRules.categoryClearedStatus")}
                        </span>
                      )}
                    </div>
                  </div>

                  <div className="w-full grid grid-cols-3 gap-4">
                    <Select
                      label={t("templates.form.type")}
                      placeholder={t("templates.form.typePlaceholder")}
                      disabled={!canWrite()}
                      data={[
                        { value: ItemType.ITEM, label: t("items.types.item") },
                        { value: ItemType.CAGE, label: t("items.types.cage") },
                        { value: ItemType.CAGE_ITEM, label: t("items.types.cageItem") },
                      ]}
                      clearable
                      value={form.values.type ?? null}
                      error={form.errors.type}
                      onChange={(value) => form.setFieldValue("type", value)}
                    />

                    <Select
                      label={t("templates.form.retrievalStatus")}
                      placeholder={t("templates.form.statusPlaceholder")}
                      searchable
                      disabled={!canWrite()}
                      data={Object.values(RetrievalStatus).map((status) => ({
                        label: sanitize(status),
                        value: status,
                      }))}
                      value={form.values.retrieval_status ?? null}
                      error={form.errors.retrieval_status}
                      clearable
                      onChange={(value) => form.setFieldValue("retrieval_status", value)}
                    />

                    <Select
                      label={t("templates.form.maintenanceStatus")}
                      placeholder={t("templates.form.statusPlaceholder")}
                      searchable
                      disabled={!canWrite()}
                      data={Object.values(MaintenanceStatus).map((status) => ({
                        label: sanitize(status),
                        value: status,
                      }))}
                      value={form.values.maintenance_status ?? null}
                      error={form.errors.maintenance_status}
                      clearable
                      onChange={(value) => form.setFieldValue("maintenance_status", value)}
                    />
                  </div>

                  <div className="w-full grid grid-cols-2 gap-4">
                    <TextInput
                      label={t("templates.form.location")}
                      disabled={!canWrite() || templateQuery.isLoading || templateMutation.isPending}
                      placeholder={t("templates.form.locationPlaceholder")}
                      {...form.getInputProps("location")}
                    />

                    <TextInput
                      label={t("templates.form.manufacturerEmail")}
                      disabled={!canWrite() || templateQuery.isLoading || templateMutation.isPending}
                      placeholder={t("templates.form.manufacturerEmailPlaceholder")}
                      type="email"
                      {...form.getInputProps("notify_email")}
                    />
                  </div>
                </div>
              </Accordion.Panel>
            </Accordion.Item>

            <Accordion.Item value="customize_columns">
              <Accordion.Control>
                <div className="flex items-center gap-2">
                  <span>{t("common.customFields")}</span>
                  <PermissionTooltip disabled={!canWrite()}>
                    <ActionButton
                      color="black"
                      size="xs"
                      icon={IconPlus}
                      onClick={(e) => {
                        e.stopPropagation();
                        const existing = { ...form.values.customize_columns };
                        let newKey = `field_${Object.keys(existing).length + 1}`;
                        while (newKey in existing) {
                          newKey += "_";
                        }
                        form.setFieldValue("customize_columns", {
                          ...existing,
                          [newKey]: "",
                        });
                      }}
                      disabled={!canWrite()}
                    />
                  </PermissionTooltip>
                </div>
              </Accordion.Control>

              <Accordion.Panel>
                {Object.entries(form.values.customize_columns ?? {}).length <=
                0 ? (
                  <div>{t("common.addCustomFieldHint")}</div>
                ) : (
                  <div className="w-full flex flex-col gap-2">
                    {Object.entries(form.values.customize_columns ?? {}).map(
                      ([key, value], index) => {
                        const fieldId = `${key}_${index}`;

                        return (
                          <div
                            key={fieldId}
                            className="w-full grid grid-cols-[auto_1fr_1fr_auto] gap-2 items-center"
                          >
                            <span className="bg-black/5 font-bold text-black/70 rounded-full p-2 w-8 h-8 flex items-center justify-center">
                              <span>{index + 1}</span>
                            </span>
                            <TextInput
                              defaultValue={key}
                              disabled={!canWrite()}
                              onBlur={(event) => {
                                const newKey = event.currentTarget.value.trim();
                                if (!newKey || newKey === key) return;

                                const updated = {
                                  ...form.values.customize_columns,
                                };
                                const val = updated[key];
                                delete updated[key];

                                if (newKey in updated) return;

                                updated[newKey] = val;
                                form.setFieldValue(
                                  "customize_columns",
                                  updated
                                );
                              }}
                            />

                            <TextInput
                              value={value}
                              disabled={!canWrite()}
                              onChange={(event) => {
                                form.setFieldValue(
                                  `customize_columns.${key}`,
                                  event.currentTarget.value
                                );
                              }}
                            />

                            <PermissionTooltip disabled={!canWrite()}>
                              <ActionButton
                                icon={IconTrash}
                                variant="white"
                                color="red.5"
                                onClick={() => {
                                  const updated = {
                                    ...form.values.customize_columns,
                                  };
                                  delete updated[key];
                                  form.setFieldValue(
                                    "customize_columns",
                                    updated
                                  );
                                }}
                                disabled={!canWrite()}
                              />
                            </PermissionTooltip>
                          </div>
                        );
                      }
                    )}
                  </div>
                )}
              </Accordion.Panel>
            </Accordion.Item>
          </Accordion>
        </form>
      </div>
    </DetailPageLayout>
  );
};

export default TemplateDetailPage;
